import {
  ArrowLeft,
  Circle,
  CirclePause,
  Grip,
  Loader2,
  Mic,
  MicOff,
  PhoneForwarded,
  PhoneOff,
  UserPlus,
  X,
} from 'lucide-react';
import { useState } from 'react';
import type { TwilioPhone } from './useTwilioPhone';
import './incall.css';

/** The pad, with the letters every phone has carried since rotary dials. */
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

type Face = 'keypad' | 'transfer' | 'add';

/**
 * Everything an agent can do to a connected Twilio call, in one strip.
 *
 * This is the call bar's shape — a row over a thread, with a row to spend and
 * not a screen. The dashboard's softphone frame draws the same controls as a
 * phone does (`src/phone/CallScreen.tsx`); both take them from the phone
 * object and nothing else, so a control cannot reach the call some other way
 * and become a second opinion about what it is doing.
 *
 * The strip is the row of buttons; pressing Keypad, Transfer or Add opens a
 * face beneath it and the row stays, so Hang up is never more than one press
 * away. Mute and hang up are the device's own and need no server; the pad
 * sends DTMF straight into the call; pause, transfer and add go through the
 * server, which acts on the customer's leg and answers with a reason when it
 * cannot — shown here, under the row, in its own words.
 */
export function InCallActions({ phone }: { phone: TwilioPhone }) {
  const [face, setFace] = useState<Face | null>(null);
  const [digits, setDigits] = useState('');
  const [number, setNumber] = useState('');
  const [sent, setSent] = useState<string | null>(null);

  const busy = phone.controlBusy;
  // Without a leg SID or a route to the server there is nothing to control —
  // the bar shows mute and hang up alone, as it always did.
  const controls = phone.canControl;

  const toggle = (next: Face) => {
    setFace((current) => (current === next ? null : next));
    setSent(null);
  };

  const press = (key: string) => {
    phone.sendDigits(key);
    setDigits((current) => `${current}${key}`.slice(-24));
  };

  const dialable = number.replace(/[^\d+]/g, '').length >= 3;
  const submit = async () => {
    if (!dialable || busy) return;
    const to = number.trim();
    const done = face === 'transfer' ? await phone.transfer(to) : await phone.addParticipant(to);
    if (done) {
      setSent(face === 'transfer' ? `Transferring to ${to}…` : `Ringing ${to} into the call…`);
      setNumber('');
      if (face === 'add') setFace(null);
    }
  };

  return (
    <div className="incall">
      <div className="incall-row">
        <button
          type="button"
          className={`incall-btn${phone.muted ? ' on' : ''}`}
          onClick={phone.toggleMute}
          title={phone.muted ? 'Unmute' : 'Mute'}
        >
          {phone.muted ? <MicOff size={13} /> : <Mic size={13} />}
          <span>{phone.muted ? 'Unmute' : 'Mute'}</span>
        </button>
        <button
          type="button"
          className={`incall-btn${face === 'keypad' ? ' on' : ''}`}
          onClick={() => toggle('keypad')}
          title="Keypad — send digits into the call"
        >
          <Grip size={13} />
          <span>Keypad</span>
        </button>
        {controls && (
          <>
            <button
              type="button"
              className={`incall-btn${phone.recording === 'paused' ? ' on' : ''}`}
              disabled={busy}
              onClick={phone.recording === 'paused' ? phone.resumeRecording : phone.pauseRecording}
              title={
                phone.recording === 'paused'
                  ? 'Recording is paused — resume it'
                  : 'Pause the recording (the pause is left out of the file)'
              }
            >
              {phone.recording === 'paused' ? <Circle size={13} /> : <CirclePause size={13} />}
              <span>{phone.recording === 'paused' ? 'Resume rec' : 'Pause rec'}</span>
            </button>
            <button
              type="button"
              className={`incall-btn${face === 'transfer' ? ' on' : ''}`}
              disabled={busy || phone.conferenced}
              onClick={() => toggle('transfer')}
              title={
                phone.conferenced
                  ? 'A conference cannot be transferred — hang up to leave it'
                  : 'Transfer — hand the caller to another number'
              }
            >
              <PhoneForwarded size={13} />
              <span>Transfer</span>
            </button>
            <button
              type="button"
              className={`incall-btn${face === 'add' ? ' on' : ''}`}
              disabled={busy}
              onClick={() => toggle('add')}
              title="Add — ring another number into this call"
            >
              <UserPlus size={13} />
              <span>Add</span>
            </button>
          </>
        )}
        <button type="button" className="incall-btn danger" onClick={phone.hangUp} title="Hang up">
          <PhoneOff size={13} />
          <span>Hang up</span>
        </button>
      </div>

      {(phone.controlError || (sent && phone.participants.length === 0)) && (
        <div className={`incall-note${phone.controlError ? ' warn' : ''}`}>
          {phone.controlError ?? sent}
        </div>
      )}

      {phone.participants.length > 0 && (
        // Who was added and where each stands, refreshed while the room is
        // up — a person who picked up must stop reading as ringing.
        <ul className="incall-people">
          {phone.participants.map((person) => (
            <li key={person.callSid} className={`incall-person ${person.status}`}>
              {person.status === 'ringing' ? (
                <Loader2 size={12} className="incall-spin" />
              ) : (
                <span className={`incall-dot ${person.status}`} />
              )}
              <span className="incall-person-who">{person.to}</span>
              <span className="incall-person-state">
                {person.status === 'ringing' ? 'Ringing…' : person.status === 'connected' ? 'In call' : 'Left'}
              </span>
              {person.status !== 'ended' && (
                <button
                  type="button"
                  className="incall-remove"
                  disabled={busy}
                  title={person.status === 'ringing' ? 'Stop ringing them' : 'Remove from the call'}
                  onClick={() => void phone.removeParticipant(person.callSid)}
                >
                  <X size={12} />
                </button>
              )}
            </li>
          ))}
          <li className="incall-people-note">Hanging up ends the call for everyone.</li>
        </ul>
      )}

      {face === 'keypad' && (
        <div className="incall-face">
          <div className="incall-digits" aria-live="polite">
            {digits || <span className="incall-placeholder">Press keys to send tones</span>}
          </div>
          <div className="incall-grid">
            {KEYS.map((key) => (
              <button key={key} type="button" className="incall-key" onClick={() => press(key)}>
                {key}
              </button>
            ))}
          </div>
        </div>
      )}

      {(face === 'transfer' || face === 'add') && (
        <div className="incall-face">
          <div className="incall-entry">
            <button
              type="button"
              className="incall-btn incall-back"
              title="Back"
              onClick={() => setFace(null)}
            >
              <ArrowLeft size={13} />
            </button>
            <input
              className="incall-input"
              value={number}
              inputMode="tel"
              autoFocus
              placeholder={face === 'transfer' ? 'Number to transfer to' : 'Number to add'}
              aria-label={face === 'transfer' ? 'Number to transfer to' : 'Number to add'}
              onChange={(event) => setNumber(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void submit();
              }}
            />
            <button
              type="button"
              className="incall-btn primary"
              disabled={!dialable || busy}
              onClick={() => void submit()}
            >
              {face === 'transfer' ? <PhoneForwarded size={13} /> : <UserPlus size={13} />}
              <span>{face === 'transfer' ? 'Transfer' : 'Add'}</span>
            </button>
          </div>
          <div className="incall-hint">
            {face === 'transfer'
              ? 'The caller is handed to this number and your leg ends. They see your channel number.'
              : 'The number is rung into this call. The first person added turns it into a conference.'}
          </div>
        </div>
      )}
    </div>
  );
}
